
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      name: 'María García',
      location: 'Eixample, Barcelona',
      text: 'Excelente trabajo en la instalación de tabiques en mi piso. El equipo fue muy profesional, limpio y terminaron antes de lo previsto. El acabado quedó perfecto para pintar.',
      rating: 5,
      project: 'Tabiques de pladur',
    },
    {
      name: 'Carlos Martínez',
      location: 'Sant Andreu, Barcelona',
      text: 'Contraté sus servicios para insonorizar mi estudio de música. El resultado ha sido increíble, la reducción de ruido es notable. Muy recomendables.',
      rating: 5,
      project: 'Aislamiento acústico',
    },
    {
      name: 'Laura Fernández',
      location: 'Gràcia, Barcelona',
      text: 'Instalaron el falso techo en mi oficina con gran profesionalidad. Trabajaron en horario flexible para no interrumpir nuestra actividad. Muy satisfecha.',
      rating: 5,
      project: 'Falso techo',
    },
    {
      name: 'Jordi Puig',
      location: 'Les Corts, Barcelona',
      text: 'Reforma completa del baño con pladur antihumedad. Cumplieron con los plazos y el presupuesto. El trato fue excelente desde el primer contacto.',
      rating: 5,
      project: 'Pladur antihumedad',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-primary-dark text-5xl md:text-6xl font-serif font-bold mb-6">
            Lo que dicen nuestros clientes
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            La satisfacción de nuestros clientes es nuestra mejor carta de presentación. Descubre por qué confían en PLADUREX.
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer"
            aria-label="Testimonio anterior"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer"
            aria-label="Siguiente testimonio"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          {/* Testimonial Card */}
          <div className="overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="bg-bg-light rounded-2xl p-8 md:p-12 shadow-sm"
              >
                {/* Quote Icon */}
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <i className="ri-double-quotes-l text-primary text-3xl"></i>
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-700 text-lg md:text-xl text-center leading-relaxed mb-8">
                  "{testimonials[currentIndex].text}"
                </p>

                {/* Rating */}
                <div className="flex items-center justify-center space-x-1 mb-6">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>

                {/* Client Info */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-xl font-bold">
                      {testimonials[currentIndex].name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <h3 className="text-primary-dark text-lg font-bold">
                    {testimonials[currentIndex].name}
                  </h3>
                  <p className="text-gray-500 text-sm mb-2">
                    {testimonials[currentIndex].location}
                  </p>
                  <span className="inline-block bg-primary/10 text-primary text-xs font-medium px-3 py-1 rounded-full">
                    {testimonials[currentIndex].project}
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots Navigation */}
          <div className="flex items-center justify-center space-x-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentIndex
                    ? 'bg-primary w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Ir al testimonio ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
